import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, User, Building, MessageSquare, Loader2 } from "lucide-react";
import { SectionHeader } from "./Services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const isValid =
        form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email);

    const submit = async (e) => {
        e.preventDefault();
        if (!isValid || loading) return;
        setLoading(true);
        try {
            await axios.post(`${BACKEND_URL}/api/leads`, form);
            setDone(true);
            toast.success("Request received", {
                description: "Our team will reach out within one business day.",
            });
            setForm({ name: "", email: "", company: "", message: "" });
        } catch (err) {
            toast.error("Could not submit", {
                description: "Please try again, or email hello@proksyresearch.com.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="lead-form"
            className="relative py-28"
            data-testid="lead-form-section"
        >
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
            <div className="absolute -top-20 left-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="pr-container relative grid lg:grid-cols-2 gap-12 items-center">
                <div>
                    <SectionHeader
                        overline="Request insights"
                        title="Tell us about your study"
                        subtitle="Share a few details and we'll respond with a tailored proposal — sample, methodology, timeline and price."
                    />
                    <ul className="mt-8 space-y-3 text-sm text-slate-300">
                        {[
                            "Tailored sample plan in 24 hours",
                            "Pricing aligned to your scope",
                            "NDA available on request",
                        ].map((b) => (
                            <li key={b} className="flex items-center gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                {b}
                            </li>
                        ))}
                    </ul>
                </div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="pr-glass-strong rounded-3xl p-7 sm:p-8 space-y-4 relative"
                    data-testid="lead-form"
                >
                    <Field
                        icon={<User className="w-4 h-4 text-blue-300" />}
                        label="Full name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Jane Doe"
                        testId="lead-name"
                    />
                    <Field
                        icon={<Mail className="w-4 h-4 text-blue-300" />}
                        label="Work email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="jane@company.com"
                        testId="lead-email"
                    />
                    <Field
                        icon={<Building className="w-4 h-4 text-blue-300" />}
                        label="Company"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Company name"
                        testId="lead-company"
                    />
                    <div>
                        <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                            Message
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-3.5 pointer-events-none">
                                <MessageSquare className="w-4 h-4 text-blue-300" />
                            </span>
                            <textarea
                                name="message"
                                rows={4}
                                value={form.message}
                                onChange={handleChange}
                                placeholder="Tell us about your audience, geography and timeline."
                                className="input-glow w-full rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500 resize-none"
                                data-testid="lead-message"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={!isValid || loading}
                        className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="lead-submit"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                        {done ? "Sent — we'll be in touch" : "Request Insights"}
                    </button>
                    <p className="text-[11px] font-mono-pr text-slate-500 text-center">
                        We never share your data. NDA available on request.
                    </p>
                </motion.form>
            </div>
        </section>
    );
}

export function Field({
    icon,
    label,
    name,
    type = "text",
    value,
    onChange,
    placeholder,
    testId,
}) {
    return (
        <div>
            <label className="block font-outfit text-xs uppercase tracking-[0.22em] text-blue-300 mb-2">
                {label}
            </label>
            <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {icon}
                </span>
                <input
                    name={name}
                    type={type}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className="input-glow w-full rounded-xl py-3 pl-10 pr-4 text-sm text-white placeholder:text-slate-500"
                    data-testid={testId}
                />
            </div>
        </div>
    );
}
